import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Building2,
  Users,
  Search,
  LogOut,
  LogIn,
  ChevronRight,
  Crown,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  RefreshCw,
  DollarSign,
  TrendingUp,
  CreditCard,
  RotateCcw,
  ArrowDownCircle,
  Server,
  Cpu,
  Phone,
  Brain,
  Settings,
  X,
  PiggyBank,
  Wallet,
  Shield,
  ShieldCheck,
  ShieldOff,
  Copy,
  Eye,
  EyeOff,
  Image,
  Upload,
  Trash2,
  MessageCircle,
  Plane,
  Edit,
  Save,
  Lock
} from '../../icons/wp-icons';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import * as superAdminApi from '../../api/superAdminApi';
import { toast } from 'react-hot-toast';

// ─── Agent-Prompt Modal (HEYKIKI Sub-Org) ────────────────────────
//
// Lädt den aktuell aktiven + den auto-generierten ElevenLabs-System-Prompt
// einer HeyKiki-Sub-Org und erlaubt dem Super-Admin, einen manuellen Prompt
// zu setzen oder zurückzusetzen. Beim Speichern wird der ElevenLabs-Agent
// synchron synchronisiert (Sync-Failures gehen NICHT still verloren).
//
// Tool-Liste, transfer_to_number-Conditions und end_call bleiben weiterhin
// an die Kiki-Zentrale-Settings gebunden — das Modal überschreibt nur den
// Text-Block. Die Wissensbasis (`knowledgeBase`) wird automatisch unten
// angehängt und muss nicht in den Manual-Prompt einkopiert werden.
const AgentPromptModal = ({ org, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null); // { generated, active, manualSystemPrompt, manualPromptLocked, ... }
  const [view, setView] = useState('manual'); // 'manual' | 'generated' | 'active'
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await superAdminApi.getAgentPrompt(org.id);
        if (cancelled) return;
        setData(result);
        // Default-Tab: wenn Manual gesetzt → bearbeiten, sonst Auto-generiert
        // als Vorlage für ersten Edit.
        const startManual = result.manualSystemPrompt || result.generated?.system_prompt || '';
        setDraft(startManual);
        setView('manual');
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || 'Prompt konnte nicht geladen werden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [org.id]);

  const handleSave = async () => {
    if (!confirm('Manuellen Prompt speichern und an ElevenLabs synchronisieren?')) return;
    setSaving(true);
    try {
      const result = await superAdminApi.setAgentPrompt(org.id, {
        manualSystemPrompt: draft,
      });
      toast.success('Prompt gespeichert + ElevenLabs synchronisiert');
      // Refresh
      const refreshed = await superAdminApi.getAgentPrompt(org.id);
      setData(refreshed);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!confirm('Manuellen Prompt löschen — der Agent nutzt dann wieder die Auto-Generierung aus den Kiki-Zentrale-Einstellungen. Fortfahren?')) return;
    setSaving(true);
    try {
      await superAdminApi.setAgentPrompt(org.id, { manualSystemPrompt: null });
      toast.success('Manueller Prompt zurückgesetzt');
      const refreshed = await superAdminApi.getAgentPrompt(org.id);
      setData(refreshed);
      setDraft(refreshed.generated?.system_prompt || '');
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Reset fehlgeschlagen');
    }
    setSaving(false);
  };

  const dirty = data?.manualSystemPrompt !== draft && draft.trim().length > 0;
  const hasManual = !!data?.manualSystemPrompt;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80">
      <div className="bg-gray-800 rounded-xl w-full max-w-5xl border border-gray-700 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              Systemprompt — {org.name}
            </h3>
            <p className="text-xs text-gray-400 mt-1">
              {hasManual
                ? <span className="text-amber-300">Manueller Prompt aktiv — Auto-Generierung deaktiviert</span>
                : <span className="text-green-400">Auto-Generierung aktiv (aus Kiki-Zentrale)</span>}
              {data?.manualPromptUpdatedAt && (
                <span className="ml-2 text-gray-500">
                  · zuletzt geändert {new Date(data.manualPromptUpdatedAt).toLocaleString('de-DE')}
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="animate-spin rounded-full h-8 w-8 border-4 border-gray-600 border-t-purple-500"></span>
          </div>
        ) : error ? (
          <div className="p-6 text-red-400">{error}</div>
        ) : (
          <>
            {/* Tab-Switcher */}
            <div className="flex items-center gap-1 px-4 pt-3 border-b border-gray-700">
              <button
                onClick={() => setView('manual')}
                className={`px-3 py-2 text-sm rounded-t-lg transition-colors ${view === 'manual' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                Manueller Prompt {dirty && <span className="text-amber-400">●</span>}
              </button>
              <button
                onClick={() => setView('generated')}
                className={`px-3 py-2 text-sm rounded-t-lg transition-colors ${view === 'generated' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                Auto-generiert (Vorschau)
              </button>
              {hasManual && (
                <button
                  onClick={() => setView('active')}
                  className={`px-3 py-2 text-sm rounded-t-lg transition-colors ${view === 'active' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  Aktuell aktiv
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden p-4">
              {view === 'manual' && (
                <div className="h-full flex flex-col">
                  <p className="text-xs text-gray-400 mb-2">
                    Dieser Text ersetzt den Auto-generierten Prompt vollständig. Tools, Notdienst-
                    Weiterleitung und end_call bleiben dynamisch aus der Kiki-Zentrale. Die
                    Wissensbasis (knowledgeBase) wird automatisch unten angehängt — nicht hier
                    einkopieren.
                  </p>
                  <textarea
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    spellCheck={false}
                    className="flex-1 w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-100 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    style={{ minHeight: '50vh' }}
                  />
                  <div className="text-xs text-gray-500 mt-1 flex items-center justify-between">
                    <span>{draft.length.toLocaleString('de-DE')} Zeichen</span>
                    <span>Max 60.000 Zeichen</span>
                  </div>
                </div>
              )}
              {view === 'generated' && (
                <div className="h-full overflow-y-auto" style={{ maxHeight: '60vh' }}>
                  <p className="text-xs text-gray-400 mb-2">
                    So sähe der Prompt aktuell aus, wenn die Auto-Generierung greift (basierend
                    auf den Kiki-Zentrale-Einstellungen).
                  </p>
                  <pre className="p-3 bg-gray-900 border border-gray-700 rounded-lg text-gray-300 text-xs font-mono whitespace-pre-wrap">{data.generated?.system_prompt || '(nicht verfügbar)'}</pre>
                </div>
              )}
              {view === 'active' && (
                <div className="h-full overflow-y-auto" style={{ maxHeight: '60vh' }}>
                  <p className="text-xs text-gray-400 mb-2">
                    So sieht der Prompt aktuell aus, wie er an ElevenLabs ausgespielt wird (Manual + Wissensbasis).
                  </p>
                  <pre className="p-3 bg-gray-900 border border-gray-700 rounded-lg text-gray-300 text-xs font-mono whitespace-pre-wrap">{data.active?.system_prompt || '(nicht verfügbar)'}</pre>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-700">
              <div>
                {hasManual && (
                  <button
                    onClick={handleReset}
                    disabled={saving}
                    className="px-3 py-2 text-sm text-amber-300 hover:text-amber-200 disabled:opacity-50 flex items-center gap-1"
                    title="Manuellen Prompt löschen — Auto-Generierung aktivieren"
                  >
                    <RotateCcw className="w-4 h-4" /> Auf Auto zurücksetzen
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
                >
                  Schließen
                </button>
                {view === 'manual' && (
                  <button
                    onClick={handleSave}
                    disabled={saving || !draft.trim()}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    {saving
                      ? <RefreshCw className="w-4 h-4 animate-spin" />
                      : <Save className="w-4 h-4" />}
                    Speichern + an ElevenLabs senden
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Org-Detail Modal Komponente ─────────────────────────────────
const OrgDetailModal = ({ detailOrg, setDetailOrg, detailLoading, isHeyKikiScope, getStatusBadge, resetPasswordValue, setResetPasswordValue, resetPasswordLoading, handleResetPassword, deactivateLoading, handleDeactivateOrg, setOrganisations }) => {
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState(detailOrg.name || '');
  const [editAgentId, setEditAgentId] = useState(detailOrg.kiAgentSettings?.elevenLabsAgentId || '');
  const [editEmail, setEditEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [recentCalls, setRecentCalls] = useState(null);
  const [callsLoading, setCallsLoading] = useState(false);
  const [showTranscript, setShowTranscript] = useState(null);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [lockToggling, setLockToggling] = useState(false);

  const isHeyKikiSubOrg = detailOrg.orgType === 'HEYKIKI';
  const kikiLocked = !!detailOrg.kiAgentSettings?.manualPromptLocked;
  const hasManualPrompt = !!detailOrg.kiAgentSettings?.manualSystemPrompt;

  const refreshDetail = async () => {
    try {
      const details = await superAdminApi.getOrganisationDetails(detailOrg.id);
      setDetailOrg(prev => ({ ...prev, ...details }));
    } catch (e) {
      // Ignorieren — Hauptaktion ist schon erfolgreich
    }
  };

  const handleToggleLock = async () => {
    const next = !kikiLocked;
    if (!confirm(next
      ? `Kiki-Zentrale für "${detailOrg.name}" sperren? Der Handwerker kann danach KEINE Einstellungen mehr ändern (nur lesen).`
      : `Kiki-Zentrale für "${detailOrg.name}" entsperren? Der Handwerker kann danach wieder selbst Einstellungen ändern.`)) return;
    setLockToggling(true);
    try {
      await superAdminApi.setKikiZentraleLock(detailOrg.id, next);
      toast.success(next ? 'Kiki-Zentrale gesperrt' : 'Kiki-Zentrale entsperrt');
      await refreshDetail();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Sperre konnte nicht geändert werden');
    }
    setLockToggling(false);
  };

  // Admin-Email initialisieren wenn Benutzer geladen
  useEffect(() => {
    const admin = detailOrg.benutzer?.find(u => u.isOrganisationOwner);
    if (admin && !editEmail) setEditEmail(admin.email);
  }, [detailOrg.benutzer]);

  // Edit-Felder aktualisieren wenn Org sich ändert
  useEffect(() => {
    setEditName(detailOrg.name || '');
    setEditAgentId(detailOrg.kiAgentSettings?.elevenLabsAgentId || '');
  }, [detailOrg.name, detailOrg.kiAgentSettings?.elevenLabsAgentId]);

  const loadRecentCalls = async () => {
    setCallsLoading(true);
    try {
      const data = await superAdminApi.getRecentCalls(detailOrg.id);
      setRecentCalls(data.calls);
    } catch (error) {
      toast.error('Anrufe konnten nicht geladen werden');
    }
    setCallsLoading(false);
  };

  const handleSaveChanges = async () => {
    const changes = {};
    if (editName.trim() && editName.trim() !== detailOrg.name) changes.orgName = editName.trim();
    if (isHeyKikiScope && editAgentId.trim() !== (detailOrg.kiAgentSettings?.elevenLabsAgentId || '')) changes.elevenlabsAgentId = editAgentId.trim();
    const admin = detailOrg.benutzer?.find(u => u.isOrganisationOwner);
    if (admin && editEmail.trim() && editEmail.trim() !== admin.email) changes.loginEmail = editEmail.trim();

    if (Object.keys(changes).length === 0) {
      setEditMode(false);
      return;
    }

    setSaving(true);
    try {
      await superAdminApi.updateOrg(detailOrg.id, changes);
      toast.success('Änderungen gespeichert');
      setEditMode(false);
      // Detail + Liste neu laden
      const [details, orgsData] = await Promise.all([
        superAdminApi.getOrganisationDetails(detailOrg.id),
        superAdminApi.getOrganisations()
      ]);
      setDetailOrg(prev => ({ ...prev, ...details, name: details.name }));
      setOrganisations(orgsData.organisations);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-3xl border border-gray-700 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              {editMode ? (
                <input value={editName} onChange={e => setEditName(e.target.value)} className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              ) : detailOrg.name}
            </h3>
            {detailOrg.heykikiOrgId && (
              <p className="text-xs text-purple-400 font-mono mt-1">HeyKiki-ID: {detailOrg.heykikiOrgId}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!editMode ? (
              <button onClick={() => setEditMode(true)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors flex items-center gap-1">
                <Settings className="w-3 h-3" /> Bearbeiten
              </button>
            ) : (
              <>
                <button onClick={handleSaveChanges} disabled={saving} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1">
                  {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} Speichern
                </button>
                <button onClick={() => setEditMode(false)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors">
                  Abbrechen
                </button>
              </>
            )}
            <button onClick={() => setDetailOrg(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {detailLoading ? (
          <div className="flex items-center justify-center py-12">
            <span className="animate-spin rounded-full h-8 w-8 border-4 border-gray-600 border-t-blue-500"></span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Info-Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Status</p>
                <div>{getStatusBadge(detailOrg.planStatus)}</div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-1">Erstellt am</p>
                <p className="text-sm text-white">{new Date(detailOrg.createdAt).toLocaleDateString('de-DE')}</p>
              </div>

              {isHeyKikiScope && (
                <div className="bg-gray-700/50 rounded-lg p-4 col-span-2">
                  <p className="text-xs text-gray-400 mb-1">Agent-ID</p>
                  {editMode ? (
                    <input value={editAgentId} onChange={e => setEditAgentId(e.target.value)} placeholder="agent_..." className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  ) : (
                    <p className="text-sm text-white font-mono break-all">{detailOrg.kiAgentSettings?.elevenLabsAgentId || '-'}</p>
                  )}
                </div>
              )}

              {isHeyKikiScope && detailOrg.kiAgentSettings && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Letzter Sync</p>
                  <p className="text-sm text-white">
                    {detailOrg.kiAgentSettings.elevenLabsSyncedAt ? new Date(detailOrg.kiAgentSettings.elevenLabsSyncedAt).toLocaleString('de-DE') : 'Noch nie'}
                  </p>
                  {detailOrg.kiAgentSettings.elevenLabsSyncError && (
                    <p className="text-xs text-red-400 mt-1">{detailOrg.kiAgentSettings.elevenLabsSyncError}</p>
                  )}
                </div>
              )}
              {isHeyKikiScope && detailOrg.voiceAssistantSettings && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Telefonnummer</p>
                  <p className="text-sm text-white">{detailOrg.voiceAssistantSettings.phoneNumber || '-'}</p>
                </div>
              )}
              {isHeyKikiScope && detailOrg.callStats && (
                <div className="bg-gray-700/50 rounded-lg p-4 col-span-2">
                  <p className="text-xs text-gray-400 mb-1">Anrufe</p>
                  <p className="text-sm text-white">{detailOrg.callStats.total} gesamt, {detailOrg.callStats.thisMonth} diesen Monat
                    {detailOrg.callStats.lastCallAt && <span className="text-gray-400 ml-2">(letzter: {new Date(detailOrg.callStats.lastCallAt).toLocaleString('de-DE')})</span>}
                  </p>
                </div>
              )}
            </div>

            {/* Benutzer */}
            {detailOrg.benutzer && (
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Benutzer ({detailOrg.benutzer.length})</h4>
                <div className="bg-gray-700/30 rounded-lg divide-y divide-gray-600">
                  {detailOrg.benutzer.map(user => (
                    <div key={user.id} className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <span className="text-white text-sm">{user.name || user.email}</span>
                        {editMode && user.isOrganisationOwner ? (
                          <input value={editEmail} onChange={e => setEditEmail(e.target.value)} className="ml-2 bg-gray-600 border border-gray-500 rounded px-2 py-0.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-56" />
                        ) : (
                          <span className="text-gray-400 text-xs ml-2">{user.email}</span>
                        )}
                        {user.isOrganisationOwner && <Crown className="w-3 h-3 text-yellow-500 inline ml-1" title="Owner" />}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${user.aktiv ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                        {user.aktiv ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Zahlen */}
            {detailOrg._count && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-white">{detailOrg._count.kunden}</p>
                  <p className="text-xs text-gray-400">Kunden</p>
                </div>
                <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-white">{detailOrg._count.anfragen}</p>
                  <p className="text-xs text-gray-400">Anfragen</p>
                </div>
                <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-white">{detailOrg._count.termine}</p>
                  <p className="text-xs text-gray-400">Termine</p>
                </div>
              </div>
            )}

            {/* Letzte Anrufe */}
            {isHeyKikiScope && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Letzte Anrufe
                  </h4>
                  {!recentCalls && (
                    <button onClick={loadRecentCalls} disabled={callsLoading} className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg transition-colors flex items-center gap-1">
                      {callsLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Phone className="w-3 h-3" />} Laden
                    </button>
                  )}
                </div>
                {callsLoading && (
                  <div className="flex items-center justify-center py-6">
                    <span className="animate-spin rounded-full h-6 w-6 border-4 border-gray-600 border-t-blue-500"></span>
                  </div>
                )}
                {recentCalls && recentCalls.length > 0 && (
                  <div className="bg-gray-700/30 rounded-lg divide-y divide-gray-600 max-h-64 overflow-y-auto">
                    {recentCalls.map(call => (
                      <div key={call.id} className="px-4 py-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Phone className={`w-3 h-3 ${call.direction === 'outbound' ? 'text-blue-400' : 'text-green-400'}`} />
                            <span className="text-white text-sm font-medium">{call.callerNumber}</span>
                            {call.kunde && <span className="text-gray-400 text-xs">({call.kunde.name})</span>}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <span>{formatDuration(call.durationSeconds)}</span>
                            <span>{new Date(call.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                        {call.summary && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{call.summary}</p>
                        )}
                        {call.transcript && (
                          <button
                            onClick={() => setShowTranscript(showTranscript === call.id ? null : call.id)}
                            className="text-xs text-blue-400 hover:text-blue-300 mt-1"
                          >
                            {showTranscript === call.id ? 'Transkript verbergen' : 'Transkript anzeigen'}
                          </button>
                        )}
                        {showTranscript === call.id && call.transcript && (
                          <pre className="mt-2 p-3 bg-gray-800 rounded text-xs text-gray-300 whitespace-pre-wrap max-h-48 overflow-y-auto">{call.transcript}</pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {recentCalls && recentCalls.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">Keine Anrufe vorhanden</p>
                )}
              </div>
            )}

            {/* Aktionen */}
            <div className="border-t border-gray-700 pt-4 space-y-4">
              <h4 className="text-sm font-medium text-gray-300">Aktionen</h4>

              {/* HEYKIKI Sub-Org-spezifische Aktionen — sichtbar in beiden
                  Super-Admin-Dashboards (HeyKiki-Scope und WerkPilot-Scope),
                  solange die Org orgType=HEYKIKI hat. */}
              {isHeyKikiSubOrg && (
                <div className="space-y-2 p-3 bg-purple-900/20 border border-purple-700/40 rounded-lg">
                  <p className="text-xs text-purple-300 font-medium flex items-center gap-1">
                    <Brain className="w-3.5 h-3.5" /> ElevenLabs-Agent
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPromptModalOpen(true)}
                      className={`py-2 text-sm rounded-lg transition-colors flex items-center justify-center gap-2 ${hasManualPrompt
                        ? 'bg-amber-600 hover:bg-amber-700 text-white'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'}`}
                      title={hasManualPrompt
                        ? 'Manueller Prompt aktiv — bearbeiten oder zurücksetzen'
                        : 'Manuellen Systemprompt setzen (überschreibt Auto-Generierung)'}
                    >
                      <Edit className="w-4 h-4" />
                      {hasManualPrompt ? 'Systemprompt bearbeiten (manuell aktiv)' : 'Systemprompt bearbeiten'}
                    </button>
                    <button
                      onClick={handleToggleLock}
                      disabled={lockToggling}
                      className={`py-2 text-sm rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${kikiLocked
                        ? 'bg-green-600/20 hover:bg-green-600/40 text-green-400 border border-green-600/30'
                        : 'bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/30'}`}
                      title={kikiLocked
                        ? 'Sperre aufheben — Handwerker darf wieder Einstellungen ändern'
                        : 'Kiki-Zentrale sperren — Handwerker kann nichts mehr ändern'}
                    >
                      {lockToggling
                        ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></span>
                        : kikiLocked
                          ? <><ShieldOff className="w-4 h-4" /> Kiki-Zentrale entsperren</>
                          : <><Lock className="w-4 h-4" /> Kiki-Zentrale sperren</>}
                    </button>
                  </div>
                  {kikiLocked && (
                    <p className="text-xs text-amber-300 mt-1">
                      ⚠ Kiki-Zentrale ist gesperrt — der Handwerker kann KEINE Einstellungen ändern.
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Admin-Passwort zurücksetzen</label>
                  <input type="text" value={resetPasswordValue} onChange={e => setResetPasswordValue(e.target.value)} placeholder="Neues Passwort (mind. 8 Zeichen)" className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <button onClick={handleResetPassword} disabled={resetPasswordLoading || resetPasswordValue.length < 8} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1">
                  {resetPasswordLoading ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span> : 'Zurücksetzen'}
                </button>
              </div>
              {detailOrg.benutzer && (() => {
                const allInactive = detailOrg.benutzer.every(u => !u.aktiv);
                return (
                  <button onClick={handleDeactivateOrg} disabled={deactivateLoading} className={`w-full py-2 text-sm rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${allInactive ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/30'}`}>
                    {deactivateLoading ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></span> : allInactive ? <><CheckCircle className="w-4 h-4" /> Organisation reaktivieren</> : <><XCircle className="w-4 h-4" /> Organisation deaktivieren</>}
                  </button>
                );
              })()}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-700">
          <button onClick={() => setDetailOrg(null)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">Schließen</button>
        </div>
      </div>

      {promptModalOpen && (
        <AgentPromptModal
          org={detailOrg}
          onClose={() => setPromptModalOpen(false)}
          onSaved={refreshDetail}
        />
      )}
    </div>
  );
};

const SuperAdminDashboard = () => {
  const { superAdmin, loading: authLoading, logout, impersonate, isAuthenticated, isHeyKikiScope } = useSuperAdmin();
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [refunds, setRefunds] = useState(null);
  const [costs, setCosts] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOrgType, setFilterOrgType] = useState('');
  const [impersonating, setImpersonating] = useState(null);
  const [showRefunds, setShowRefunds] = useState(false);
  const [showCostsModal, setShowCostsModal] = useState(false);
  const [costsForm, setCostsForm] = useState({ hetznerCosts: '', otherCosts: '', otherCostsNote: '' });
  const [savingCosts, setSavingCosts] = useState(false);
  const [syncingRefunds, setSyncingRefunds] = useState(false);
  const [revenueFilter, setRevenueFilter] = useState({ year: null, months: 6, previousMonth: false });

  // Org-Detail Modal (HeyKiki)
  const [detailOrg, setDetailOrg] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  // Passwort ändern Modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [changePwLoading, setChangePwLoading] = useState(false);

  // 2FA State
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableTotpCode, setDisableTotpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // Chat-Icon State
  const [showChatIconModal, setShowChatIconModal] = useState(false);
  const [chatIconPreview, setChatIconPreview] = useState(null);
  const [chatIconUploading, setChatIconUploading] = useState(false);

  // Branchen-Icons State
  const [showBrancheIconsModal, setShowBrancheIconsModal] = useState(false);
  const [brancheIcons, setBrancheIcons] = useState({});
  const [brancheIconsLoading, setBrancheIconsLoading] = useState(false);
  const [uploadingBranche, setUploadingBranche] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Basis-Daten immer laden
      const promises = [
        superAdminApi.getStats(),
        superAdminApi.getOrganisations(),
      ];

      // Revenue/Refunds/Costs nur für WerkPilot-Scope laden
      if (!isHeyKikiScope) {
        promises.push(
          superAdminApi.getRevenueStats().catch(err => {
            console.warn('Revenue stats not available:', err);
            return null;
          }),
          superAdminApi.getRefunds({ limit: 20 }).catch(err => {
            console.warn('Refunds not available:', err);
            return null;
          }),
          superAdminApi.getCosts().catch(err => {
            console.warn('Costs not available:', err);
            return null;
          })
        );
      }

      const results = await Promise.all(promises);
      setStats(results[0]);
      setOrganisations(results[1].organisations);

      if (!isHeyKikiScope) {
        setRevenue(results[2]);
        setRefunds(results[3]);
        setCosts(results[4]);
        if (results[4]) {
          setCostsForm({
            hetznerCosts: results[4].costs.hetzner || '',
            otherCosts: results[4].costs.other || '',
            otherCostsNote: results[4].costs.otherNote || ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    }
    setLoading(false);

  };

  const handleSaveCosts = async () => {
    setSavingCosts(true);
    try {
      const now = new Date();
      await superAdminApi.saveCosts({
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        hetznerCosts: parseFloat(costsForm.hetznerCosts) || 0,
        otherCosts: parseFloat(costsForm.otherCosts) || 0,
        otherCostsNote: costsForm.otherCostsNote || null
      });
      toast.success('Kosten gespeichert');
      setShowCostsModal(false);
      // Kosten neu laden
      const costsData = await superAdminApi.getCosts();
      setCosts(costsData);
    } catch (error) {
      console.error('Error saving costs:', error);
      toast.error('Fehler beim Speichern');
    }
    setSavingCosts(false);
  };

  // 2FA Funktionen
  const load2FAStatus = async () => {
    try {
      const status = await superAdminApi.get2FAStatus();
      setTwoFAStatus(status);
    } catch (error) {
      console.error('Error loading 2FA status:', error);
    }
  };

  const start2FASetup = async () => {
    setTwoFALoading(true);
    try {
      const data = await superAdminApi.setup2FA();
      setTwoFASetup(data);
      setTotpCode('');
    } catch (error) {
      toast.error(error.response?.data?.error || '2FA-Setup fehlgeschlagen');
    }
    setTwoFALoading(false);
  };

  const verify2FASetup = async () => {
    if (totpCode.length < 6) return;
    setTwoFALoading(true);
    try {
      const data = await superAdminApi.verifySetup2FA(totpCode);
      setBackupCodes(data.backupCodes);
      setTwoFASetup(null);
      setTotpCode('');
      load2FAStatus();
      toast.success('2FA erfolgreich aktiviert!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Verifizierung fehlgeschlagen');
    }
    setTwoFALoading(false);
  };

  const disable2FA = async () => {
    if (!disablePassword) {
      toast.error('Passwort erforderlich');
      return;
    }
    setTwoFALoading(true);
    try {
      await superAdminApi.disable2FA(disablePassword, disableTotpCode);
      setTwoFAStatus({ enabled: false, verified: false, backupCodesRemaining: 0 });
      setDisablePassword('');
      setDisableTotpCode('');
      setShow2FAModal(false);
      toast.success('2FA deaktiviert');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Deaktivierung fehlgeschlagen');
    }
    setTwoFALoading(false);
  };

  const open2FAModal = async () => {
    setShow2FAModal(true);
    setTwoFASetup(null);
    setBackupCodes(null);
    setTotpCode('');
    setDisablePassword('');
    setDisableTotpCode('');
    await load2FAStatus();
  };

  // Branchen-Icons Funktionen
  const BRANCHEN_LIST = [
    { code: 'glaserei', label: 'Glaserei', emoji: '🪟' },
    { code: 'tischlerei', label: 'Tischlerei', emoji: '🪑' },
    { code: 'schreinerei', label: 'Schreinerei', emoji: '🪚' },
    { code: 'sanitaer', label: 'Sanitär', emoji: '🚿' },
    { code: 'elektro', label: 'Elektro', emoji: '⚡' },
    { code: 'maler', label: 'Maler', emoji: '🖌️' },
    { code: 'dachdecker', label: 'Dachdecker', emoji: '🏠' },
    { code: 'fliesenleger', label: 'Fliesenleger', emoji: '🧱' },
    { code: 'metallbau', label: 'Metallbau', emoji: '⚙️' },
    { code: 'zimmerei', label: 'Zimmerei', emoji: '🪵' },
    { code: 'trockenbau', label: 'Trockenbau', emoji: '🏗️' },
    { code: 'bodenleger', label: 'Bodenleger', emoji: '🪵' },
    { code: 'gartenbau', label: 'Gartenbau', emoji: '🌳' },
    { code: 'klimatechnik', label: 'Klimatechnik', emoji: '❄️' },
    { code: 'gebaeudereinigung', label: 'Gebäudereinigung', emoji: '🧹' },
    { code: 'kfz', label: 'KFZ', emoji: '🚗' },
    { code: 'sonstiges', label: 'Sonstiges', emoji: '🔧' }
  ];

  const loadBrancheIcons = async () => {
    setBrancheIconsLoading(true);
    try {
      const data = await superAdminApi.getBrancheIcons();
      // Icons als Map speichern für einfachen Zugriff
      const iconsMap = {};
      if (data.icons) {
        data.icons.forEach(icon => {
          iconsMap[icon.brancheCode] = icon;
        });
      }
      setBrancheIcons(iconsMap);
    } catch (error) {
      console.error('Error loading branche icons:', error);
      toast.error('Fehler beim Laden der Branchen-Icons');
    }
    setBrancheIconsLoading(false);
  };

  const handleChangePassword = async () => {
    if (newPw !== confirmPw) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (newPw.length < 8) {
      toast.error('Neues Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    setChangePwLoading(true);
    try {
      await superAdminApi.changePassword(currentPw, newPw);
      toast.success('Passwort erfolgreich geändert');
      setShowPasswordModal(false);
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Passwort-Änderung fehlgeschlagen');
    }
    setChangePwLoading(false);
  };

  const openBrancheIconsModal = async () => {
    setShowBrancheIconsModal(true);
    await loadBrancheIcons();
  };

  const handleBrancheIconUpload = async (brancheCode, file) => {
    if (!file) return;

    // Validierung
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Nur PNG, JPG, SVG und WebP sind erlaubt');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Maximale Dateigröße: 2MB');
      return;
    }

    setUploadingBranche(brancheCode);
    try {
      await superAdminApi.uploadBrancheIcon(brancheCode, file);
      toast.success(`Icon für ${brancheCode} hochgeladen`);
      await loadBrancheIcons();
    } catch (error) {
      console.error('Error uploading icon:', error);
      toast.error('Fehler beim Hochladen');
    }
    setUploadingBranche(null);
  };

  const handleBrancheIconDelete = async (brancheCode) => {
    if (!confirm(`Icon für "${brancheCode}" wirklich löschen?`)) return;

    try {
      await superAdminApi.deleteBrancheIcon(brancheCode);
      toast.success('Icon gelöscht');
      await loadBrancheIcons();
    } catch (error) {
      console.error('Error deleting icon:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Chat-Icon Funktionen
  const loadChatIcon = async () => {
    try {
      const data = await superAdminApi.getChatIcon();
      setChatIconPreview(data.chatIconPath || null);
    } catch (error) {
      console.error('Error loading chat icon:', error);
    }
  };

  const openChatIconModal = async () => {
    setShowChatIconModal(true);
    await loadChatIcon();
  };

  const handleChatIconUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Nur PNG, JPG, SVG und WebP sind erlaubt');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Maximale Dateigröße: 2MB');
      return;
    }

    setChatIconUploading(true);
    try {
      const result = await superAdminApi.uploadChatIcon(file);
      setChatIconPreview(result.chatIconPath);
      toast.success('Chat-Icon hochgeladen');
    } catch (error) {
      console.error('Error uploading chat icon:', error);
      toast.error('Fehler beim Hochladen');
    }
    setChatIconUploading(false);
  };

  const handleChatIconDelete = async () => {
    if (!confirm('Chat-Icon wirklich löschen?')) return;

    try {
      await superAdminApi.deleteChatIcon();
      setChatIconPreview(null);
      toast.success('Chat-Icon gelöscht');
    } catch (error) {
      console.error('Error deleting chat icon:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const copyBackupCodes = () => {
    if (backupCodes) {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      toast.success('Backup-Codes kopiert');
    }
  };

  const loadRevenueWithFilter = async (filter) => {
    try {
      const revenueData = await superAdminApi.getRevenueStats(filter);
      setRevenue(revenueData);
      setRevenueFilter(filter);
    } catch (error) {
      console.error('Error loading revenue:', error);
      toast.error('Fehler beim Laden der Umsatzdaten');
    }
  };

  const handleSyncRefunds = async () => {
    setSyncingRefunds(true);
    try {
      const result = await superAdminApi.syncRefunds();
      toast.success(`${result.created} neue Refunds synchronisiert`);
      // Refunds und Revenue neu laden
      const [refundsData, revenueData, costsData] = await Promise.all([
        superAdminApi.getRefunds({ limit: 20 }),
        superAdminApi.getRevenueStats(),
        superAdminApi.getCosts()
      ]);
      setRefunds(refundsData);
      setRevenue(revenueData);
      setCosts(costsData);
    } catch (error) {
      console.error('Error syncing refunds:', error);
      toast.error('Fehler beim Synchronisieren');
    }
    setSyncingRefunds(false);
  };

  const handleImpersonate = async (org) => {
    if (impersonating) return;
    setImpersonating(org.id);

    const result = await impersonate(org.id);
    if (!result.success) {
      toast.error(result.error);
      setImpersonating(null);
    }
  };

  // Agent Resync (HeyKiki)
  const [resyncingOrg, setResyncingOrg] = useState(null);
  const handleResyncAgent = async (org) => {
    if (resyncingOrg) return;
    setResyncingOrg(org.id);
    try {
      const result = await superAdminApi.resyncAgent(org.id);
      if (result.success) {
        toast.success(`Agent für "${org.name}" synchronisiert`);
        // Org-Liste neu laden für aktuellen Sync-Status
        const orgsData = await superAdminApi.getOrganisations();
        setOrganisations(orgsData.organisations);
      } else {
        toast.error(result.message || 'Sync fehlgeschlagen');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Sync fehlgeschlagen');
    }
    setResyncingOrg(null);
  };

  // Org-Detail Modal öffnen
  const openOrgDetail = async (org) => {
    setDetailLoading(true);
    setDetailOrg(org); // Erstmal die Basisdaten anzeigen
    setResetPasswordValue('');
    try {
      const details = await superAdminApi.getOrganisationDetails(org.id);
      setDetailOrg({ ...org, ...details });
    } catch (error) {
      console.error('Error loading org details:', error);
      toast.error('Details konnten nicht geladen werden');
    }
    setDetailLoading(false);
  };

  const handleDeactivateOrg = async () => {
    if (!detailOrg) return;
    const allInactive = detailOrg.benutzer?.every(u => !u.aktiv);
    const action = allInactive ? 'reactivate' : 'deactivate';
    const label = allInactive ? 'reaktivieren' : 'deaktivieren';

    if (!confirm(`Organisation "${detailOrg.name}" wirklich ${label}?`)) return;

    setDeactivateLoading(true);
    try {
      if (allInactive) {
        await superAdminApi.reactivateOrg(detailOrg.id);
        toast.success('Organisation reaktiviert');
      } else {
        await superAdminApi.deactivateOrg(detailOrg.id);
        toast.success('Organisation deaktiviert');
      }
      // Details und Org-Liste neu laden
      const [details, orgsData] = await Promise.all([
        superAdminApi.getOrganisationDetails(detailOrg.id),
        superAdminApi.getOrganisations()
      ]);
      setDetailOrg({ ...detailOrg, ...details });
      setOrganisations(orgsData.organisations);
    } catch (error) {
      toast.error(error.response?.data?.error || `${label} fehlgeschlagen`);
    }
    setDeactivateLoading(false);
  };

  const handleResetPassword = async () => {
    if (!detailOrg || !resetPasswordValue) return;
    if (resetPasswordValue.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setResetPasswordLoading(true);
    try {
      const result = await superAdminApi.resetOrgPassword(detailOrg.id, resetPasswordValue);
      toast.success(`Passwort für ${result.userEmail} zurückgesetzt`);
      setResetPasswordValue('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Passwort-Reset fehlgeschlagen');
    }
    setResetPasswordLoading(false);
  };

  // Filter-Logik
  const filteredOrgs = organisations.filter((org) => {
    const matchesSearch =
      !searchTerm ||
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.admin?.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPlan = !filterPlan || org.plan === filterPlan;
    const matchesStatus = !filterStatus || org.planStatus === filterStatus;
    const matchesOrgType = !filterOrgType || org.orgType === filterOrgType;

    return matchesSearch && matchesPlan && matchesStatus && matchesOrgType;
  });

  const getStatusBadge = (status) => {
    const config = {
      TRIAL: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Trial' },
      ACTIVE: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Aktiv' },
      PAST_DUE: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, label: 'Zahlung offen' },
      CANCELLED: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Gekündigt' },
      SUSPENDED: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Gesperrt' }
    };
    const c = config[status] || config.TRIAL;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.color}`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const colors = {
      STARTER: 'bg-gray-100 text-gray-700',
      PROFESSIONAL: 'bg-purple-100 text-purple-700',
      BUSINESS: 'bg-orange-100 text-orange-700',
      ENTERPRISE: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[plan] || colors.STARTER}`}>
        {plan}
      </span>
    );
  };

  // Auth Loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  // Nicht authentifiziert -> zum Login
  if (!isAuthenticated) {
    return <Navigate to="/super-admin/login" replace />;
  }

  // Data Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className={`${isHeyKikiScope ? 'bg-[#1a2e1a]' : 'bg-gray-800'} border-b ${isHeyKikiScope ? 'border-green-800/50' : 'border-gray-700'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg overflow-hidden">
                {isHeyKikiScope ? (
                  <div className="w-10 h-10 bg-[#81c264] rounded-lg flex items-center justify-center text-white font-bold text-lg">HK</div>
                ) : (
                  <img src="/icons/icon-512x512.png" alt="Werkpilot" className="w-10 h-10 object-contain" />
                )}
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {isHeyKikiScope ? 'HeyKiki Admin Panel' : 'Super-Admin Panel'}
                </h1>
                <p className="text-sm text-gray-400">Eingeloggt als {superAdmin?.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isHeyKikiScope && (
                <>
                  <button
                    onClick={() => window.open('/cockpit-preview.html', '_blank')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors shadow-lg shadow-blue-600/20"
                    title="Cockpit Live-Preview in neuem Tab öffnen"
                  >
                    <Plane className="w-4 h-4" />
                    Cockpit Demo
                  </button>
                  <button
                    onClick={openChatIconModal}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    title="KI Chat-Icon verwalten"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Chat-Icon
                  </button>
                  <button
                    onClick={openBrancheIconsModal}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    title="Branchen-Icons verwalten"
                  >
                    <Image className="w-4 h-4" />
                    Branchen-Icons
                  </button>
                </>
              )}
              <button
                onClick={() => { setShowPasswordModal(true); setCurrentPw(''); setNewPw(''); setConfirmPw(''); }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                title="Eigenes Passwort ändern"
              >
                <Eye className="w-4 h-4" />
                Passwort
              </button>
              <button
                onClick={open2FAModal}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                title="Zwei-Faktor-Authentifizierung"
              >
                <Shield className="w-4 h-4" />
                2FA
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600/20 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.totalOrganisations}</p>
                  <p className="text-sm text-gray-400">Organisationen</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-600/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.activeOrganisations}</p>
                  <p className="text-sm text-gray-400">Aktive Abos</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-600/20 rounded-lg">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
                  <p className="text-sm text-gray-400">Benutzer gesamt</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-600/20 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.totalKunden}</p>
                  <p className="text-sm text-gray-400">Kunden gesamt</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Revenue Stats (nur WerkPilot) */}
        {!isHeyKikiScope && revenue && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 rounded-xl p-4 border border-green-600/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-600/30 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{revenue.mrr.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                  <p className="text-sm text-gray-400">MRR (Brutto)</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-xl p-4 border border-blue-600/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600/30 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{revenue.arr.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                  <p className="text-sm text-gray-400">ARR (Annual)</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-600/20 to-red-800/20 rounded-xl p-4 border border-red-600/30 cursor-pointer hover:border-red-500/50 transition-colors"
                 onClick={() => !syncingRefunds && setShowRefunds(!showRefunds)}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-600/30 rounded-lg">
                  <RotateCcw className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">-{(revenue.refundsThisMonth || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                  <p className="text-sm text-gray-400">Refunds (diesen Monat)</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 rounded-xl p-4 border border-purple-600/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-600/30 rounded-lg">
                  <CreditCard className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{revenue.activeSubscriptions}</p>
                  <p className="text-sm text-gray-400">Aktive Abos</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profit & Costs Section (nur WerkPilot) */}
        {!isHeyKikiScope && costs && (
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <PiggyBank className="w-5 h-5 text-yellow-400" />
                Gewinn & Kosten (aktueller Monat)
              </h3>
              <button
                onClick={() => setShowCostsModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
                Kosten bearbeiten
              </button>
            </div>

            {/* Profit Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className={`rounded-xl p-4 border ${costs.profit >= 0 ? 'bg-green-600/10 border-green-600/30' : 'bg-red-600/10 border-red-600/30'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${costs.profit >= 0 ? 'bg-green-600/30' : 'bg-red-600/30'}`}>
                    <Wallet className={`w-6 h-6 ${costs.profit >= 0 ? 'text-green-400' : 'text-red-400'}`} />
                  </div>
                  <div>
                    <p className={`text-3xl font-bold ${costs.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {costs.profit >= 0 ? '+' : ''}{costs.profit.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                    </p>
                    <p className="text-sm text-gray-400">Gewinn ({costs.profitMargin}% Marge)</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600/30 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{costs.revenue.net.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                    <p className="text-sm text-gray-400">Netto-Umsatz</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-600/30 rounded-lg">
                    <Cpu className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{costs.costs.total.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                    <p className="text-sm text-gray-400">Gesamtkosten</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                <div className="flex items-center gap-2 mb-1">
                  <Brain className="w-4 h-4 text-purple-400" />
                  <span className="text-xs text-gray-400">OpenAI</span>
                </div>
                <p className="text-lg font-semibold text-white">{costs.costs.openai.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
              </div>

              <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                <div className="flex items-center gap-2 mb-1">
                  <Phone className="w-4 h-4 text-green-400" />
                  <span className="text-xs text-gray-400">ElevenLabs</span>
                </div>
                <p className="text-lg font-semibold text-white">{(costs.costs.elevenLabs || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                {costs.meta?.elevenLabsCredits != null && (
                  <p className="text-xs text-gray-500" title={`${costs.meta.elevenLabsEurPer1kCredits} € / 1.000 Credits`}>
                    {Math.round(costs.meta.elevenLabsCredits).toLocaleString('de-DE')} Credits
                  </p>
                )}
              </div>

              <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                <div className="flex items-center gap-2 mb-1">
                  <Phone className="w-4 h-4 text-red-400" />
                  <span className="text-xs text-gray-400">Twilio ({costs.meta.activePhoneNumbers} Nr.)</span>
                </div>
                <p className="text-lg font-semibold text-white">{costs.costs.twilio.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
              </div>

              <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                <div className="flex items-center gap-2 mb-1">
                  <Image className="w-4 h-4 text-yellow-400" />
                  <span className="text-xs text-gray-400">KIE AI</span>
                </div>
                <p className="text-lg font-semibold text-white">{(costs.costs.kieAi || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
              </div>

              <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                <div className="flex items-center gap-2 mb-1">
                  <Server className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-gray-400">Hetzner</span>
                </div>
                <p className="text-lg font-semibold text-white">{costs.costs.hetzner.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
              </div>

              <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                <div className="flex items-center gap-2 mb-1">
                  <Cpu className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-400">Sonstige</span>
                </div>
                <p className="text-lg font-semibold text-white">{costs.costs.other.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                {costs.costs.otherNote && (
                  <p className="text-xs text-gray-500 truncate" title={costs.costs.otherNote}>{costs.costs.otherNote}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Refunds Section (nur WerkPilot) */}
        {!isHeyKikiScope && showRefunds && refunds && (
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-red-400" />
                Rückerstattungen
              </h3>
              <div className="flex items-center gap-4 text-sm">
                <button
                  onClick={handleSyncRefunds}
                  disabled={syncingRefunds}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${syncingRefunds ? 'animate-spin' : ''}`} />
                  {syncingRefunds ? 'Sync...' : 'Stripe Sync'}
                </button>
                <span className="text-gray-400">
                  Gesamt: <span className="text-red-400 font-semibold">{refunds.stats.totalAmount.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                  <span className="text-gray-500 ml-1">({refunds.stats.totalCount} Refunds)</span>
                </span>
                <span className="text-gray-400">
                  Diesen Monat: <span className="text-red-400 font-semibold">{refunds.stats.thisMonthAmount.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                  <span className="text-gray-500 ml-1">({refunds.stats.thisMonthCount})</span>
                </span>
              </div>
            </div>

            {refunds.refunds.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">Datum</th>
                      <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">Organisation</th>
                      <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">Betrag</th>
                      <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">Grund</th>
                      <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {refunds.refunds.map((refund) => (
                      <tr key={refund.id} className="hover:bg-gray-750">
                        <td className="px-3 py-2 text-sm text-gray-300">
                          {new Date(refund.stripeCreatedAt).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <span className="text-white">{refund.organisation?.name || 'Unbekannt'}</span>
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <span className="text-red-400 font-medium">{refund.amountFormatted}</span>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-400">
                          {refund.reason === 'requested_by_customer' && 'Kundenanfrage'}
                          {refund.reason === 'duplicate' && 'Doppelzahlung'}
                          {refund.reason === 'fraudulent' && 'Betrug'}
                          {!refund.reason && '-'}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            refund.status === 'succeeded' ? 'bg-green-600/20 text-green-400' :
                            refund.status === 'pending' ? 'bg-yellow-600/20 text-yellow-400' :
                            'bg-gray-600/20 text-gray-400'
                          }`}>
                            {refund.status === 'succeeded' ? 'Abgeschlossen' :
                             refund.status === 'pending' ? 'Ausstehend' : refund.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4">Keine Rückerstattungen vorhanden</p>
            )}
          </div>
        )}

        {/* Revenue History (nur WerkPilot) */}
        {!isHeyKikiScope && revenue?.revenueHistory && (
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-400" />
                Umsatzentwicklung
              </h3>
              <div className="flex items-center gap-2">
                <select
                  value={revenueFilter.year || ''}
                  onChange={(e) => loadRevenueWithFilter({
                    year: e.target.value ? parseInt(e.target.value) : null,
                    months: e.target.value ? 12 : revenueFilter.months,
                    previousMonth: false
                  })}
                  className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Letzte Monate</option>
                  {(revenue.filter?.availableYears || [new Date().getFullYear()]).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {!revenueFilter.year && (
                  <select
                    value={revenueFilter.previousMonth ? 'prev' : revenueFilter.months}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'prev') {
                        loadRevenueWithFilter({ year: null, months: 1, previousMonth: true });
                      } else {
                        loadRevenueWithFilter({ year: null, months: parseInt(val), previousMonth: false });
                      }
                    }}
                    className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value={1}>Aktueller Monat</option>
                    <option value="prev">Letzter Monat</option>
                    <option value={3}>3 Monate</option>
                    <option value={6}>6 Monate</option>
                    <option value={12}>12 Monate</option>
                    <option value={24}>24 Monate</option>
                  </select>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 mb-4 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-green-500 rounded"></span>
                <span className="text-gray-400">Brutto-Umsatz</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-500 rounded"></span>
                <span className="text-gray-400">Refunds</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-blue-500 rounded"></span>
                <span className="text-gray-400">Netto</span>
              </span>
            </div>
            {revenue.revenueHistory.length > 0 ? (
            <div className="flex items-end gap-2 h-48">
              {revenue.revenueHistory.map((item, index) => {
                const maxAmount = Math.max(...revenue.revenueHistory.map(h => h.gross || h.amount || 0));
                const grossPercent = maxAmount > 0 ? ((item.gross || item.amount || 0) / maxAmount) * 100 : 0;
                const refundPercent = maxAmount > 0 ? ((item.refunds || 0) / maxAmount) * 100 : 0;
                const gross = item.gross || item.amount || 0;
                const refundsAmount = item.refunds || 0;
                const net = item.net || (gross - refundsAmount);
                return (
                  <div key={item.month} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex flex-col items-center justify-end h-36">
                      <div className="text-xs text-gray-400 mb-1 text-center">
                        <span className="text-green-400">{gross.toLocaleString('de-DE', { minimumFractionDigits: 0 })} €</span>
                        {refundsAmount > 0 && (
                          <span className="text-red-400 ml-1">-{refundsAmount.toLocaleString('de-DE', { minimumFractionDigits: 0 })}</span>
                        )}
                      </div>
                      <div className="w-full flex gap-0.5 items-end h-28">
                        <div
                          className="flex-1 bg-gradient-to-t from-green-600 to-green-400 rounded-t transition-all"
                          style={{ height: `${Math.max(grossPercent, 5)}%` }}
                          title={`Brutto: ${gross.toLocaleString('de-DE')} €`}
                        />
                        {refundsAmount > 0 && (
                          <div
                            className="flex-1 bg-gradient-to-t from-red-600 to-red-400 rounded-t transition-all"
                            style={{ height: `${Math.max(refundPercent, 3)}%` }}
                            title={`Refunds: ${refundsAmount.toLocaleString('de-DE')} €`}
                          />
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {item.month.split('-')[1]}/{item.month.split('-')[0].slice(2)}
                    </span>
                  </div>
                );
              })}
            </div>
            ) : (
              <p className="text-gray-400 text-center py-8">Keine Umsatzdaten vorhanden</p>
            )}
          </div>
        )}

        {/* Filter & Search */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Suche nach Name, Slug oder E-Mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Alle Pläne</option>
              <option value="STARTER">Starter</option>
              <option value="PROFESSIONAL">Professional</option>
              <option value="BUSINESS">Business</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Alle Status</option>
              <option value="TRIAL">Trial</option>
              <option value="ACTIVE">Aktiv</option>
              <option value="PAST_DUE">Zahlung offen</option>
              <option value="CANCELLED">Gekündigt</option>
              <option value="SUSPENDED">Gesperrt</option>
            </select>

            {!isHeyKikiScope && (
              <select
                value={filterOrgType}
                onChange={(e) => setFilterOrgType(e.target.value)}
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">Alle Typen</option>
                <option value="STANDALONE">Standalone</option>
                <option value="HEYKIKI">HeyKiki</option>
              </select>
            )}

            <button
              onClick={loadData}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Aktualisieren
            </button>
          </div>
        </div>

        {/* Organisations Table */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-750">
                <tr className="border-b border-gray-700">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Organisation</th>
                  {!isHeyKikiScope && <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Plan</th>}
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Status</th>
                  {isHeyKikiScope && <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Agent-ID</th>}
                  {isHeyKikiScope && <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Sync</th>}
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">{isHeyKikiScope ? 'Telefon' : 'Telefonassistent'}</th>
                  {isHeyKikiScope && <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Anrufe</th>}
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Kunden</th>
                  {!isHeyKikiScope && <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Benutzer</th>}
                  {!isHeyKikiScope && <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">KI-Anfragen</th>}
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Erstellt</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-300">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredOrgs.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-750 transition-colors">
                    <td className="px-4 py-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <span
                            className="font-medium text-white hover:text-blue-400 cursor-pointer transition-colors"
                            onClick={() => openOrgDetail(org)}
                            title="Details anzeigen"
                          >{org.name}</span>
                          {org.orgType === 'HEYKIKI' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30" title={`HeyKiki Org-ID: ${org.heykikiOrgId || '—'}`}>
                              HeyKiki
                            </span>
                          )}
                          {org.admin?.isOrganisationOwner && (
                            <Crown className="w-4 h-4 text-yellow-500" title="Organisation Owner" />
                          )}
                        </div>
                        <div className="text-sm text-gray-400">{org.admin?.email || 'Kein Admin'}</div>
                        {isHeyKikiScope && org.heykikiOrgId && (
                          <div className="text-xs text-purple-400 mt-0.5 font-mono" title={`HeyKiki Org-ID: ${org.heykikiOrgId}`}>
                            ID: {org.heykikiOrgId.slice(0, 12)}{org.heykikiOrgId.length > 12 ? '...' : ''}
                          </div>
                        )}
                        {org.telefon && (
                          <a
                            href={`tel:${org.telefon}`}
                            className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 mt-0.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="w-3 h-3" />
                            {org.telefon}
                          </a>
                        )}
                      </div>
                    </td>
                    {!isHeyKikiScope && <td className="px-4 py-4">{getPlanBadge(org.plan)}</td>}
                    <td className="px-4 py-4">{getStatusBadge(org.planStatus)}</td>
                    {isHeyKikiScope && (
                      <td className="px-4 py-4">
                        {org.agentId ? (
                          <span className="text-xs font-mono text-gray-300 bg-gray-700 px-2 py-1 rounded" title={org.agentId}>
                            {org.agentId.slice(0, 12)}...
                          </span>
                        ) : (
                          <span className="text-gray-500 text-sm">-</span>
                        )}
                      </td>
                    )}
                    {isHeyKikiScope && (
                      <td className="px-4 py-4">
                        {org.agentSyncError ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" title={org.agentSyncError}>
                            <AlertTriangle className="w-3 h-3" />
                            Fehler
                          </span>
                        ) : org.agentSyncedAt ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800" title={`Letzter Sync: ${new Date(org.agentSyncedAt).toLocaleString('de-DE')}`}>
                            <CheckCircle className="w-3 h-3" />
                            OK
                          </span>
                        ) : (
                          <span className="text-gray-500 text-sm">-</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-4">
                      {org.voiceAssistantSettings?.subscriptionStatus === 'ACTIVE' ? (
                        <div className="flex items-center gap-1">
                          <Phone className="w-4 h-4 text-green-400" />
                          <span className="text-green-400 text-sm font-medium">
                            {org.voiceAssistantSettings.phoneNumber || 'Aktiv'}
                          </span>
                        </div>
                      ) : org.voiceAssistantSettings?.subscriptionStatus === 'CANCELLED' ? (
                        <div className="flex items-center gap-1">
                          <Phone className="w-4 h-4 text-yellow-400" />
                          <span className="text-yellow-400 text-xs">Gekündigt</span>
                        </div>
                      ) : org.voiceAssistantSettings?.enabled ? (
                        <div className="flex items-center gap-1">
                          <Phone className="w-4 h-4 text-blue-400" />
                          <span className="text-blue-400 text-sm font-medium">
                            {org.voiceAssistantSettings.phoneNumber || 'Aktiv'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                    {isHeyKikiScope && (
                      <td className="px-4 py-4">
                        {org.callStats ? (
                          <div>
                            <span className="text-white font-medium">{org.callStats.total}</span>
                            <span className="text-gray-500 text-xs ml-1">gesamt</span>
                            {org.callStats.thisMonth > 0 && (
                              <div className="text-xs text-green-400">+{org.callStats.thisMonth} diesen Monat</div>
                            )}
                            {org.callStats.lastCallAt && (
                              <div className="text-xs text-gray-500">
                                {new Date(org.callStats.lastCallAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-sm">-</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <span className="text-white">{org.kundenCount}</span>
                      <span className="text-gray-500"> / {org.maxKunden}</span>
                    </td>
                    {!isHeyKikiScope && (
                      <td className="px-4 py-4">
                        <span className="text-white">{org.benutzerCount}</span>
                        <span className="text-gray-500"> / {org.maxUsers}</span>
                      </td>
                    )}
                    {!isHeyKikiScope && (
                      <td className="px-4 py-4">
                        <span className="text-white">{org.usedKiAnfragen}</span>
                        <span className="text-gray-500"> / {org.maxKiAnfragen}</span>
                      </td>
                    )}
                    <td className="px-4 py-4 text-gray-400 text-sm">
                      {new Date(org.createdAt).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                      {isHeyKikiScope && (
                        <button
                          onClick={() => handleResyncAgent(org)}
                          disabled={resyncingOrg === org.id}
                          className="inline-flex items-center gap-1 px-2 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="ElevenLabs Agent neu synchronisieren"
                        >
                          <RefreshCw className={`w-3 h-3 ${resyncingOrg === org.id ? 'animate-spin' : ''}`} />
                          Sync
                        </button>
                      )}
                      <button
                        onClick={() => handleImpersonate(org)}
                        disabled={impersonating === org.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {impersonating === org.id ? (
                          <>
                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                            Lädt...
                          </>
                        ) : (
                          <>
                            <LogIn className="w-4 h-4" />
                            Einloggen
                          </>
                        )}
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filteredOrgs.length === 0 && (
                  <tr>
                    <td colSpan={isHeyKikiScope ? 9 : 9} className="px-4 py-8 text-center text-gray-400">
                      Keine Organisationen gefunden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>


        {/* Footer */}
        <div className="mt-6 text-center text-gray-500 text-sm">
          {filteredOrgs.length} von {organisations.length} Organisationen angezeigt
        </div>
      </main>

      {/* Costs Edit Modal (nur WerkPilot) */}
      {!isHeyKikiScope && showCostsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-400" />
                Monatliche Kosten bearbeiten
              </h3>
              <button
                onClick={() => setShowCostsModal(false)}
                className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  <Server className="w-4 h-4 inline mr-1" />
                  Hetzner Server-Kosten (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={costsForm.hetznerCosts}
                  onChange={(e) => setCostsForm({ ...costsForm, hetznerCosts: e.target.value })}
                  placeholder="z.B. 49.99"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  <Cpu className="w-4 h-4 inline mr-1" />
                  Sonstige Kosten (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={costsForm.otherCosts}
                  onChange={(e) => setCostsForm({ ...costsForm, otherCosts: e.target.value })}
                  placeholder="z.B. 10.00"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Beschreibung (optional)
                </label>
                <input
                  type="text"
                  value={costsForm.otherCostsNote}
                  onChange={(e) => setCostsForm({ ...costsForm, otherCostsNote: e.target.value })}
                  placeholder="z.B. Domain, SSL, etc."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="bg-gray-700/50 rounded-lg p-3 text-sm text-gray-400">
                <p className="mb-1">Automatisch erfasste Kosten:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>OpenAI: Aus API-Logs berechnet</li>
                  <li>VAPI: Aus Call-Logs aggregiert</li>
                  <li>Twilio: {costs?.meta.activePhoneNumbers || 0} Nummern × 1,99 €</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCostsModal(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveCosts}
                disabled={savingCosts}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {savingCosts ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                Zwei-Faktor-Authentifizierung
              </h3>
              <button
                onClick={() => {
                  setShow2FAModal(false);
                  setTwoFASetup(null);
                  setBackupCodes(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status anzeigen */}
            {!twoFASetup && !backupCodes && twoFAStatus && (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg flex items-center gap-3 ${
                  twoFAStatus.enabled ? 'bg-green-900/30 border border-green-700' : 'bg-yellow-900/30 border border-yellow-700'
                }`}>
                  {twoFAStatus.enabled ? (
                    <>
                      <ShieldCheck className="w-6 h-6 text-green-400" />
                      <div>
                        <p className="text-green-300 font-medium">2FA ist aktiviert</p>
                        <p className="text-sm text-green-400/70">
                          {twoFAStatus.backupCodesRemaining} Backup-Codes übrig
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <ShieldOff className="w-6 h-6 text-yellow-400" />
                      <div>
                        <p className="text-yellow-300 font-medium">2FA ist nicht aktiviert</p>
                        <p className="text-sm text-yellow-400/70">
                          Aktivieren Sie 2FA für zusätzliche Sicherheit
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {twoFAStatus.enabled ? (
                  /* 2FA deaktivieren */
                  <div className="space-y-4 mt-4">
                    <p className="text-gray-400 text-sm">
                      Um 2FA zu deaktivieren, geben Sie Ihr Passwort und einen aktuellen 2FA-Code ein.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Passwort</label>
                      <input
                        type="password"
                        value={disablePassword}
                        onChange={(e) => setDisablePassword(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Ihr Passwort"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">2FA-Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={disableTotpCode}
                        onChange={(e) => setDisableTotpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-center font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="000000"
                        maxLength={6}
                      />
                    </div>
                    <button
                      onClick={disable2FA}
                      disabled={twoFALoading || !disablePassword || disableTotpCode.length < 6}
                      className="w-full py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {twoFALoading ? (
                        <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                      ) : (
                        <>
                          <ShieldOff className="w-4 h-4" />
                          2FA deaktivieren
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  /* 2FA aktivieren */
                  <button
                    onClick={start2FASetup}
                    disabled={twoFALoading}
                    className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {twoFALoading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                    ) : (
                      <>
                        <ShieldCheck className="w-5 h-5" />
                        2FA aktivieren
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Setup-Prozess */}
            {twoFASetup && !backupCodes && (
              <div className="space-y-4">
                <p className="text-gray-300 text-sm">
                  Scannen Sie diesen QR-Code mit Ihrer Authenticator-App (z.B. Google Authenticator, Authy).
                </p>

                <div className="bg-white p-4 rounded-lg flex justify-center">
                  <img src={twoFASetup.qrCode} alt="2FA QR Code" className="w-48 h-48" />
                </div>

                <div className="bg-gray-700/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-1">Oder geben Sie diesen Code manuell ein:</p>
                  <code className="text-sm text-yellow-300 font-mono break-all">{twoFASetup.secret}</code>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Verifizierungscode eingeben
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setTwoFASetup(null)}
                    className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={verify2FASetup}
                    disabled={twoFALoading || totpCode.length < 6}
                    className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {twoFALoading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                    ) : (
                      'Verifizieren'
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Backup-Codes anzeigen */}
            {backupCodes && (
              <div className="space-y-4">
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-300 mb-2">
                    <ShieldCheck className="w-5 h-5" />
                    <span className="font-medium">2FA erfolgreich aktiviert!</span>
                  </div>
                </div>

                <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
                  <p className="text-yellow-300 font-medium mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Wichtig: Backup-Codes speichern!
                  </p>
                  <p className="text-yellow-200/70 text-sm mb-3">
                    Diese Codes werden nur einmal angezeigt. Speichern Sie sie sicher ab.
                    Sie können sie verwenden, wenn Sie keinen Zugriff auf Ihre Authenticator-App haben.
                  </p>

                  <div className="bg-gray-900 rounded-lg p-3 font-mono text-sm">
                    {showBackupCodes ? (
                      <div className="grid grid-cols-2 gap-2">
                        {backupCodes.map((code, i) => (
                          <div key={i} className="text-gray-300">{code}</div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-500 text-center py-2">
                        Codes verborgen
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setShowBackupCodes(!showBackupCodes)}
                      className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      {showBackupCodes ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {showBackupCodes ? 'Verbergen' : 'Anzeigen'}
                    </button>
                    <button
                      onClick={copyBackupCodes}
                      className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Copy className="w-4 h-4" />
                      Kopieren
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setBackupCodes(null);
                    setShow2FAModal(false);
                  }}
                  className="w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  Fertig
                </button>
              </div>
            )}

            {/* Loading State */}
            {!twoFAStatus && !twoFASetup && !backupCodes && (
              <div className="flex items-center justify-center py-8">
                <span className="animate-spin rounded-full h-8 w-8 border-4 border-gray-600 border-t-red-500"></span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Branchen-Icons Modal (nur WerkPilot) */}
      {!isHeyKikiScope && showBrancheIconsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-4xl border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Image className="w-5 h-5 text-blue-400" />
                Branchen-Icons für Onboarding
              </h3>
              <button
                onClick={() => setShowBrancheIconsModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-6">
              Hier können Sie benutzerdefinierte Icons für jede Branche hochladen.
              Diese werden im Onboarding anstelle der Standard-Emojis angezeigt und
              automatisch auf die richtige Größe skaliert.
            </p>

            {brancheIconsLoading ? (
              <div className="flex items-center justify-center py-12">
                <span className="animate-spin rounded-full h-8 w-8 border-4 border-gray-600 border-t-blue-500"></span>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {BRANCHEN_LIST.map((branche) => {
                  const hasCustomIcon = brancheIcons[branche.code];
                  const isUploading = uploadingBranche === branche.code;

                  return (
                    <div
                      key={branche.code}
                      className={`relative p-4 rounded-xl border-2 ${
                        hasCustomIcon
                          ? 'border-green-600/50 bg-green-900/10'
                          : 'border-gray-600 bg-gray-700/30'
                      }`}
                    >
                      {/* Icon Preview */}
                      <div className="flex items-center justify-center h-16 mb-3">
                        {hasCustomIcon ? (
                          <img
                            src={hasCustomIcon.iconPath}
                            alt={branche.label}
                            className="w-12 h-12 object-contain"
                          />
                        ) : (
                          <span className="text-4xl">{branche.emoji}</span>
                        )}
                      </div>

                      {/* Branche Label */}
                      <p className="text-center text-white font-medium text-sm mb-3">
                        {branche.label}
                      </p>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            className="hidden"
                            onChange={(e) => handleBrancheIconUpload(branche.code, e.target.files[0])}
                            disabled={isUploading}
                          />
                          <span
                            className={`flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-lg cursor-pointer transition-colors ${
                              isUploading
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                          >
                            {isUploading ? (
                              <span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></span>
                            ) : (
                              <Upload className="w-3 h-3" />
                            )}
                            {hasCustomIcon ? 'Ersetzen' : 'Upload'}
                          </span>
                        </label>

                        {hasCustomIcon && (
                          <button
                            onClick={() => handleBrancheIconDelete(branche.code)}
                            className="px-2 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg transition-colors"
                            title="Icon löschen"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>

                      {/* Status Badge */}
                      {hasCustomIcon && (
                        <div className="absolute top-2 right-2">
                          <span className="px-1.5 py-0.5 bg-green-600/30 text-green-400 text-xs rounded-full">
                            Custom
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-6 p-4 bg-gray-700/30 rounded-lg">
              <h4 className="text-sm font-medium text-white mb-2">Hinweise:</h4>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• Unterstützte Formate: PNG, JPG, SVG, WebP</li>
                <li>• Maximale Dateigröße: 2MB</li>
                <li>• Empfohlene Größe: 128x128 Pixel (quadratisch)</li>
                <li>• Icons werden automatisch auf die Emoji-Größe (48px) skaliert</li>
                <li>• Transparenter Hintergrund wird empfohlen</li>
              </ul>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowBrancheIconsModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Chat-Icon Modal (nur WerkPilot) */}
      {!isHeyKikiScope && showChatIconModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-400" />
                KI Chat-Icon
              </h3>
              <button
                onClick={() => setShowChatIconModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-6">
              Hier kannst du ein eigenes Icon für den KI-Chat-Button hochladen.
              Es ersetzt die Standard-Sprechblase und wird automatisch in Weiß dargestellt.
            </p>

            {/* Vorschau */}
            {chatIconPreview && (
              <div className="mb-6">
                <p className="text-xs text-gray-400 mb-2">Aktuelles Chat-Icon:</p>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-gray-700 rounded-xl flex items-center justify-center border border-gray-600">
                    <img
                      src={`${import.meta.env.VITE_API_URL?.replace('/api', '')}${chatIconPreview}`}
                      alt="Chat-Icon"
                      className="w-10 h-10 object-contain"
                      style={{ filter: 'brightness(0) invert(1)' }}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-300 mb-2">Vorschau auf dunklem Hintergrund</p>
                    <button
                      onClick={handleChatIconDelete}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 text-sm rounded-lg transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Upload */}
            <label className="block">
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => handleChatIconUpload(e.target.files[0])}
                disabled={chatIconUploading}
              />
              <span
                className={`flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                  chatIconUploading
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {chatIconUploading ? (
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                ) : (
                  <Upload className="w-5 h-5" />
                )}
                {chatIconPreview ? 'Icon ersetzen' : 'Icon hochladen'}
              </span>
            </label>

            <div className="mt-4 p-3 bg-gray-700/30 rounded-lg">
              <h4 className="text-sm font-medium text-white mb-1">Hinweise:</h4>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>Unterstützte Formate: PNG, JPG, SVG, WebP</li>
                <li>Maximale Dateigröße: 2MB</li>
                <li>Empfohlen: Quadratisches Icon, einfarbig</li>
                <li>Das Icon wird per CSS-Filter in Weiß dargestellt</li>
              </ul>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowChatIconModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Passwort ändern Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" />
                Passwort ändern
              </h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Aktuelles Passwort</label>
                <input
                  type="password"
                  value={currentPw}
                  onChange={e => setCurrentPw(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Aktuelles Passwort"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Neues Passwort</label>
                <input
                  type="password"
                  value={newPw}
                  onChange={e => setNewPw(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Neues Passwort (mind. 8 Zeichen)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Neues Passwort bestätigen</label>
                <input
                  type="password"
                  value={confirmPw}
                  onChange={e => setConfirmPw(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Passwort wiederholen"
                />
                {confirmPw && newPw !== confirmPw && (
                  <p className="text-xs text-red-400 mt-1">Passwörter stimmen nicht überein</p>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleChangePassword}
                disabled={changePwLoading || !currentPw || newPw.length < 8 || newPw !== confirmPw}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {changePwLoading ? (
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                ) : (
                  'Ändern'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Org-Detail Modal */}
      {detailOrg && <OrgDetailModal detailOrg={detailOrg} setDetailOrg={setDetailOrg} detailLoading={detailLoading} isHeyKikiScope={isHeyKikiScope} getStatusBadge={getStatusBadge} resetPasswordValue={resetPasswordValue} setResetPasswordValue={setResetPasswordValue} resetPasswordLoading={resetPasswordLoading} handleResetPassword={handleResetPassword} deactivateLoading={deactivateLoading} handleDeactivateOrg={handleDeactivateOrg} setOrganisations={setOrganisations} />}
    </div>
  );
};

export default SuperAdminDashboard;
